// Acceso a `ordenes_medio` (tabla + `ordenes_medio_vinculos` + Storage bucket `ordenes_medio` + RPC
// `guardar_orden_medio`, supabase/migrations/20261004g_ordenes_medio.sql) — fuente única (10.1). La
// RLS de las 2 tablas/bucket (`puede_ver_orden_medio`/`puede_cargar_orden_medio`/
// `puede_cargar_vinculo_orden_medio`) ya gatea QUIÉN ve/carga qué; estas funciones no reimplementan
// ese criterio, solo llaman a la base (el gate del front, para decidir si se MUESTRA el kebab, vive
// en `puedeCargarOrdenMedio` de `src/constants/permisos.js`, espejo del mismo check).
import { getImporteFacturable } from "./getImporteFacturable";
import { client } from "./supabase";

// Mismo 0.5% que valida `guardar_orden_medio` del lado del servidor — sincronizado A MANO (la base
// no puede importar JS, ver comentario de la migración).
export const TOLERANCIA_OM_PCT = 0.5;

// Slug de carpeta del bucket Storage `ordenes_medio` — EXACTO el que espera la policy de Storage
// (`CASE (storage.foldername(name))[1] WHEN 'chile' ... WHEN 'peru' ...`), nunca 'Chile'/'Perú' tal
// cual (con mayúscula/tilde) ni vía el mapeo general de getPaisCode.js (es un slug de PATH, no el
// código de país que usa el resto de la app — distinto propósito, no reimplementa 10.268).
export const folderSlugPais = (pais) => (pais === "Perú" ? "peru" : "chile");

// Paso 1 del flujo "RPC primero" (ver header de la migración, PUNTO de Storage): crea la OM + sus
// vínculos con un `archivo_path` todavía PLACEHOLDER (la fila no existe aún, no hay id real que
// poner en el path) — devuelve la fila con su `id` real, recién ahí se puede subir el PDF al path
// definitivo `{chile|peru}/{id}/archivo.pdf` (paso 2, `subirArchivoOrdenMedio`).
export const guardarOrdenMedio = async ({
  pais,
  numeroOm,
  fechaOm,
  importeNeto,
  moneda,
  archivoPath,
  archivoNombreOriginal,
  vinculos,
}) => {
  const { data, error } = await client.rpc("guardar_orden_medio", {
    p_pais: pais,
    p_numero_om: numeroOm,
    p_fecha_om: fechaOm,
    p_importe_neto: importeNeto,
    p_moneda: moneda,
    p_archivo_path: archivoPath,
    p_archivo_nombre_original: archivoNombreOriginal,
    p_vinculos: vinculos,
  });
  if (error) throw error;
  return data;
};

// Bug real (2026-10-06, OM 086028/UC Christus): el path NUNCA usa el nombre original del archivo.
// Causa confirmada con el error real de Storage (no adivinado, 10.28) — probado contra el bucket
// real con la API key anon (upload rebota por RLS antes de escribir nada, así que esto no deja
// residuo): un nombre ASCII plano (con espacios o con extensión ".PDF" en mayúscula) da el 403
// esperado de RLS, pero "Wecast_Nº_086028.PDF" (la º real, U+00BA) da
// `StorageApiError: Invalid key: .../Wecast_Nº_086028.PDF` (400) — Storage RECHAZA la key ANTES de
// llegar a RLS. El nombre original puede traer cualquier caracter (tildes, Nº, emojis) porque lo
// pone la agencia a mano — nunca se puede sanear de forma 100% confiable. Por eso el path usa un
// nombre FIJO, siempre ASCII seguro; el nombre real del archivo vive solo en
// `archivo_nombre_original` (ya se guardaba ahí, eso no cambia).
const NOMBRE_ARCHIVO_STORAGE = "om.pdf";

// Paso 2: sube el PDF al path REAL, ya con el `orden_medio_id` real devuelto por el paso 1 — así la
// policy de SELECT de Storage (que para `broker` depende de ese id para resolver su cartera) puede
// resolver la descarga más adelante. `upsert:true` — permite reintentar sobre el MISMO path si un
// intento anterior falló después de crear la fila (ver `esArchivoPendiente`/"Adjuntar PDF" abajo).
export const subirArchivoOrdenMedio = async ({ pais, ordenMedioId, file }) => {
  const path = `${folderSlugPais(pais)}/${ordenMedioId}/${NOMBRE_ARCHIVO_STORAGE}`;
  const { error } = await client.storage
    .from("ordenes_medio")
    .upload(path, file, { contentType: "application/pdf", upsert: true });
  if (error) throw error;
  return path;
};

// Placeholder usado por el paso 1 (RPC primero) antes de que exista el archivo real — mismo valor
// en guardarOrdenMedio/reemplazarOrdenMedio. Extraído a función para no repetir el string a mano.
export const archivoPendientePlaceholder = (pais) => `${folderSlugPais(pais)}/_pendiente_/placeholder.pdf`;

// ¿Esta OM quedó con la fila creada pero el PDF nunca se subió? (punto 3: "que nunca quede una OM
// sin PDF" — detectable sin columna nueva: mientras `archivo_path` siga siendo el placeholder, no
// hay archivo real en Storage). El kebab usa esto para ofrecer "Adjuntar PDF" en vez de "Ver OM".
export const esArchivoPendiente = (archivoPath) => !!archivoPath && archivoPath.includes("_pendiente_");

// Paso 3: apunta la fila ya creada (paso 1) al path real recién subido (paso 2) — usa el UPDATE que
// la migración ya concede (`ordenes_medio_update`), sin necesitar una RPC nueva.
export const actualizarArchivoOrdenMedio = async ({ id, archivoPath, archivoNombreOriginal }) => {
  const { error } = await client
    .from("ordenes_medio")
    .update({ archivo_path: archivoPath, archivo_nombre_original: archivoNombreOriginal })
    .eq("id", id);
  if (error) throw error;
};

// "Ver OM" (OM.1b punto 3) — URL firmada de descarga (bucket privado, nunca getPublicUrl, mismo
// criterio que storageFacturas.js/liquidacionesContadoraApi.js). 1h, suficiente para abrir y leer.
export const getUrlOrdenMedio = async (archivoPath) => {
  const { data, error } = await client.storage.from("ordenes_medio").createSignedUrl(archivoPath, 3600);
  if (error) throw error;
  return data.signedUrl;
};

const claveTarget = (v) => (v.origen_tipo === "sale" ? `sale:${v.sale_id}` : `contrato:${v.contrato_id}:${v.mes}`);

// Mapa { "sale:ID" | "contrato:ID:MES" -> datos de la OM vigente } de TODOS los targets dados —
// batcheado en como máximo 2 queries (10.1, mismo criterio que getLiquidacionesContadoraPorEmpleados:
// nunca N+1 por fila). Lo usa el kebab de VentasDetalle para decidir "Cargar OM" vs "Ver"/"Reemplazar".
export const getOrdenesMedioVigentesPorTargets = async ({ saleIds = [], contratoIds = [] }) => {
  const embed =
    "sale_id, contrato_id, mes, origen_tipo, " +
    "ordenes_medio(id, pais, numero_om, fecha_om, importe_neto, moneda, archivo_path, archivo_nombre_original)";
  const filas = [];
  if (saleIds.length > 0) {
    const { data, error } = await client
      .from("ordenes_medio_vinculos")
      .select(embed)
      .eq("vigente", true)
      .eq("origen_tipo", "sale")
      .in("sale_id", saleIds);
    if (error) throw error;
    filas.push(...(data || []));
  }
  if (contratoIds.length > 0) {
    const { data, error } = await client
      .from("ordenes_medio_vinculos")
      .select(embed)
      .eq("vigente", true)
      .eq("origen_tipo", "contrato_mes")
      .in("contrato_id", contratoIds);
    if (error) throw error;
    filas.push(...(data || []));
  }
  const mapa = new Map();
  filas.forEach((v) => {
    if (!v.ordenes_medio) return;
    mapa.set(claveTarget(v), {
      omId: v.ordenes_medio.id,
      pais: v.ordenes_medio.pais,
      numeroOm: v.ordenes_medio.numero_om,
      fechaOm: v.ordenes_medio.fecha_om,
      importeNeto: Number(v.ordenes_medio.importe_neto),
      moneda: v.ordenes_medio.moneda,
      archivoPath: v.ordenes_medio.archivo_path,
      archivoNombreOriginal: v.ordenes_medio.archivo_nombre_original,
    });
  });
  return mapa;
};

// Todos los vínculos VIGENTES de una OM (modo "Reemplazar" — base de targets que la nueva OM debe
// retomar, spec punto 3: "apaga vigente de los vínculos viejos e inserta los nuevos").
export const getVinculosVigentesDeOrdenMedio = async (ordenMedioId) => {
  const { data, error } = await client
    .from("ordenes_medio_vinculos")
    .select("origen_tipo, sale_id, contrato_id, mes")
    .eq("orden_medio_id", ordenMedioId)
    .eq("vigente", true);
  if (error) throw error;
  return data || [];
};

// Punto 5: ¿ya existe una OM VIGENTE con este número en este país? (detección en vivo al tipear).
// `puede_ver_orden_medio` ya gatea qué OM devuelve este SELECT — un usuario sin permiso de VER
// simplemente no la detecta (no es un bypass, mismo comportamiento que cualquier otra lectura).
export const getOrdenMedioVigentePorNumero = async ({ pais, numeroOm }) => {
  if (!numeroOm || !numeroOm.trim()) return null;
  const { data, error } = await client
    .from("ordenes_medio")
    .select("id, pais, numero_om, fecha_om, importe_neto, moneda, archivo_path, archivo_nombre_original")
    .eq("pais", pais)
    .eq("numero_om", numeroOm.trim())
    .is("reemplazada_por", null)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  return {
    omId: data.id,
    pais: data.pais,
    numeroOm: data.numero_om,
    fechaOm: data.fecha_om,
    importeNeto: Number(data.importe_neto),
    moneda: data.moneda,
    archivoPath: data.archivo_path,
    archivoNombreOriginal: data.archivo_nombre_original,
  };
};

// Suma de los netos facturables de los vínculos VIGENTES de una OM — para mostrar, en vivo, cuánto
// de la OM detectada (punto 5) ya está cubierto antes de agregar los targets nuevos. Resuelve cada
// vínculo contra `sales`/`sales_contratos` ya cargados en memoria por VentasDetalle (sin otra query),
// mismo cálculo que `ordenes_medio_importe_facturable` en SQL (10.1, getImporteFacturable.js).
export const sumarNetoVinculosExistentes = (vinculos, { oportunidades, contratos }) =>
  vinculos.reduce((total, v) => {
    if (v.origen_tipo === "sale") {
      const sale = oportunidades.find((o) => String(o.id) === String(v.sale_id));
      if (!sale) return total;
      return total + getImporteFacturable(sale);
    }
    const contrato = contratos.find((c) => String(c.id) === String(v.contrato_id));
    if (!contrato) return total;
    const mesObj = (contrato.meses || []).find((m) => m.mes === v.mes);
    if (!mesObj) return total;
    return (
      total + getImporteFacturable({ importe: mesObj.importe, descuento_acuerdo_pct: contrato.descuento_acuerdo_pct })
    );
  }, 0);

// Punto 5: agrega vínculo(s) nuevos a una OM YA EXISTENTE y vigente (en vez de crear otra con el
// mismo número, que violaría el índice único). No crea fila en `ordenes_medio` ni sube archivo.
export const vincularOrdenMedioExistente = async ({ ordenMedioId, vinculos }) => {
  const { data, error } = await client.rpc("vincular_orden_medio_existente", {
    p_orden_medio_id: ordenMedioId,
    p_vinculos: vinculos,
  });
  if (error) throw error;
  return data;
};

// Punto 3: reemplaza una OM vigente por una nueva (transaccional en la base: apaga vigente de los
// vínculos viejos + reemplazada_por de la vieja + inserta la nueva y sus vínculos). Mismo flujo
// "RPC primero" que crear: el archivo nuevo se sube DESPUÉS, con el id real de la fila nueva.
export const reemplazarOrdenMedio = async ({
  ordenMedioViejaId,
  numeroOm,
  fechaOm,
  importeNeto,
  moneda,
  archivoPath,
  archivoNombreOriginal,
  vinculos,
}) => {
  const { data, error } = await client.rpc("reemplazar_orden_medio", {
    p_orden_medio_vieja_id: ordenMedioViejaId,
    p_numero_om: numeroOm,
    p_fecha_om: fechaOm,
    p_importe_neto: importeNeto,
    p_moneda: moneda,
    p_archivo_path: archivoPath,
    p_archivo_nombre_original: archivoNombreOriginal,
    p_vinculos: vinculos,
  });
  if (error) throw error;
  return data;
};
